import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { scrollToTargetSection } from "@tCentro/Custom/Utilities/scrollToSection";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    navigationType = "navigationType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.navigationType]: "productFamilies" | "featureProduct";
    };
}

export interface CENFeatureLayoutStyles {
    titleWrapper?: InjectableCss;
    title?: InjectableCss;
}

export const cenFeatureLayoutStyle: CENFeatureLayoutStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            background: #F2F2F2;
            flex-wrap: wrap;
            margin: 0 20px 40px;

            @media (max-width: 768px) {
                margin: 0 0 40px;
                /* default: 3 or fewer items -> 33.333% each */
                & > * {
                    width: 33.333%;
                }

                /* if a 4th item exists -> 50% each */
                &:has(> :nth-child(4)) > * {
                    width: 50%;
                }
            }
        `,
    },
    title: {
        css: css`
            font-family: "Poppins";
            font-style: normal;
            font-weight: 400;
            font-size: 16px;
            color: #262525;
            line-height: 1;
            padding: 22px 24px;
            cursor: pointer;
            @media (max-width: 768px) {
                font-weight: 500;
                font-size: 14px;
                padding: 10px 5px;
                justify-content: center;
                align-items: center;
                display: flex;
                text-align: center;
            }
        `,
    },
};

const styles = cenFeatureLayoutStyle;

export const CENFeatureLayout: FC<OwnProps> = ({ fields }) => {
    const [isFaqPresent, setIsFaqPresent] = useState(false);
    const featureTitle = fields.navigationType === "featureProduct" ? "Feature Product" : "Product Families";

    useEffect(() => {
        const updateFaqPresence = () => {
            setIsFaqPresent(!!document.querySelector('[data-test-selector="brandFaqTitle"]'));
        };

        updateFaqPresence();
        const observer = new MutationObserver(updateFaqPresence);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    const navigationItems = [
        { label: featureTitle, selector: "brandProductFamilies" },
        { label: "Categories", selector: "brandCategoriesTitle" },
        { label: "Resources", selector: "brandResourceTitle" },
        ...(isFaqPresent ? [{ label: "FAQ", selector: "brandFaqTitle" }] : []),
    ];

    const handleNavigation = (selector: string, sectionTitle: string) => {
        let updateSectionTitle = sectionTitle;
        if (sectionTitle === "Categories") {
            updateSectionTitle = "Shop By Category";
        }
        scrollToTargetSection(selector, updateSectionTitle);
    };

    const handleNavigationKeyDown = (event: React.KeyboardEvent, selector: string, sectionTitle: string) => {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        event.preventDefault();
        handleNavigation(selector, sectionTitle);
    };

    return (
            <StyledWrapper as="div" {...styles.titleWrapper} className="cen-brand-intra-page-navigation">
                {navigationItems.map(item => (
                    <StyledWrapper
                        {...styles.title}
                        as="div"
                        key={item.label}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleNavigation(item.selector, item.label)}
                        onKeyDown={event => handleNavigationKeyDown(event, item.selector, item.label)}
                    >
                        {item.label}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENFeatureLayout,
    definition: {
        group: "Basic",
        displayName: "CEN Brand Intra Page Navigation",
        fieldDefinitions: [
            {
                name: fields.navigationType,
                displayName: "Navigation Type",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Product Families", value: "productFamilies" },
                    { displayName: "Feature Product", value: "featureProduct" },
                ],
                defaultValue: "productFamilies",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
